/**
 * Monitoring Service for AfterService AI Advocate
 * Provides comprehensive observability, performance tracking, and analytics
 * Maintains user privacy while gathering valuable system insights
 */

import { getMonitoringConfig, getPerformanceConfig } from '../config/config';

// Monitoring event types
export const MONITORING_EVENTS = {
  PAGE_VIEW: 'page_view',
  USER_ACTION: 'user_action',
  API_CALL: 'api_call',
  ERROR: 'error',
  PERFORMANCE: 'performance',
  FEATURE_USAGE: 'feature_usage',
  SESSION_START: 'session_start',
  SESSION_END: 'session_end'
};

// Performance metrics
export const PERFORMANCE_METRICS = {
  PAGE_LOAD_TIME: 'page_load_time',
  API_RESPONSE_TIME: 'api_response_time',
  CHAT_MESSAGE_TIME: 'chat_message_time',
  SEARCH_TIME: 'search_time',
  RENDER_TIME: 'render_time',
  BUNDLE_SIZE: 'bundle_size'
};

class MonitoringService {
  constructor() {
    this.monitoringConfig = getMonitoringConfig();
    this.performanceConfig = getPerformanceConfig();
    this.eventQueue = [];
    this.performanceQueue = [];
    this.sessionData = {
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      pageViews: 0,
      userActions: 0,
      apiCalls: 0,
      errors: 0
    };
    
    this.isEnabled = this.monitoringConfig.enableRequestTracking;
    this.batchSize = 10;
    this.flushInterval = 30000; // 30 seconds
    
    if (this.isEnabled) {
      this.initializeMonitoring();
    }
  }

  /**
   * Initialize monitoring systems
   */
  initializeMonitoring() {
    // Start performance monitoring
    this.initializePerformanceMonitoring();
    
    // Start batch processing
    this.startBatchProcessing();
    
    // Monitor page visibility for session tracking
    this.initializeSessionTracking();
    
    // Track initial page load
    this.trackPageView('/', { initial: true });
  }

  /**
   * Initialize performance monitoring
   */
  initializePerformanceMonitoring() {
    // Track page load performance
    if (window.performance && window.performance.timing) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const timing = window.performance.timing;
          const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
          
          this.trackPerformance(PERFORMANCE_METRICS.PAGE_LOAD_TIME, pageLoadTime, {
            domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
            firstPaint: timing.responseEnd - timing.navigationStart,
            networkTime: timing.responseEnd - timing.fetchStart
          });
        }, 100);
      });
    }

    // Track Core Web Vitals if available
    if ('web-vital' in window) {
      this.trackCoreWebVitals();
    }

    // Monitor memory usage
    if (performance.memory) {
      setInterval(() => {
        this.trackPerformance('memory_usage', performance.memory.usedJSHeapSize, {
          totalSize: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit
        });
      }, 60000); // Every minute
    }
  }

  /**
   * Initialize session tracking
   */
  initializeSessionTracking() {
    // Track session start
    this.trackEvent(MONITORING_EVENTS.SESSION_START, {
      sessionId: this.sessionData.sessionId,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      referrer: document.referrer
    });

    // Track session end on page unload
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });

    // Track session activity
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('session_pause', { sessionId: this.sessionData.sessionId });
      } else {
        this.trackEvent('session_resume', { sessionId: this.sessionData.sessionId });
      }
    });
  }

  /**
   * Start batch processing for events
   */
  startBatchProcessing() {
    setInterval(() => {
      this.flushEventQueue();
      this.flushPerformanceQueue();
    }, this.flushInterval);
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track page view
   */
  trackPageView(path, metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.pageViews++;
    
    this.trackEvent(MONITORING_EVENTS.PAGE_VIEW, {
      path,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      pageViews: this.sessionData.pageViews,
      ...metadata
    });
  }

  /**
   * Track user action
   */
  trackUserAction(action, category, metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.userActions++;
    
    this.trackEvent(MONITORING_EVENTS.USER_ACTION, {
      action,
      category,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      ...metadata
    });
  }

  /**
   * Track API call
   */
  trackApiCall(endpoint, method, duration, status, metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.apiCalls++;
    
    this.trackEvent(MONITORING_EVENTS.API_CALL, {
      endpoint,
      method,
      duration,
      status,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      ...metadata
    });

    // Also track as performance metric
    this.trackPerformance(PERFORMANCE_METRICS.API_RESPONSE_TIME, duration, {
      endpoint,
      method,
      status
    });
  }

  /**
   * Track error
   */
  trackError(error, context, severity = 'medium', metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.errors++;
    
    this.trackEvent(MONITORING_EVENTS.ERROR, {
      error: error.message || error.toString(),
      context,
      severity,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      stack: error.stack ? error.stack.substring(0, 500) : null, // Limit stack trace
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...metadata
    });
  }

  /**
   * Track performance metric
   */
  trackPerformance(metric, value, metadata = {}) {
    if (!this.isEnabled || !this.performanceConfig.preloadRoutes) return;

    this.performanceQueue.push({
      metric,
      value,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      url: window.location.href,
      ...metadata
    });

    // Auto-flush if queue is getting large
    if (this.performanceQueue.length >= this.batchSize * 2) {
      this.flushPerformanceQueue();
    }
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(feature, action, metadata = {}) {
    if (!this.isEnabled) return;

    this.trackEvent(MONITORING_EVENTS.FEATURE_USAGE, {
      feature,
      action,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      ...metadata
    });
  }

  /**
   * Track chat message performance
   */
  trackChatMessage(messageLength, responseTime, success, metadata = {}) {
    this.trackPerformance(PERFORMANCE_METRICS.CHAT_MESSAGE_TIME, responseTime, {
      messageLength,
      success,
      ...metadata
    });

    this.trackFeatureUsage('chat', success ? 'message_sent' : 'message_failed', {
      messageLength,
      responseTime,
      ...metadata
    });
  }

  /**
   * Track search performance
   */
  trackSearch(query, resultCount, searchTime, metadata = {}) {
    this.trackPerformance(PERFORMANCE_METRICS.SEARCH_TIME, searchTime, {
      queryLength: query.length,
      resultCount,
      ...metadata
    });

    this.trackFeatureUsage('search', 'executed', {
      queryLength: query.length,
      resultCount,
      searchTime,
      ...metadata
    });
  }

  /**
   * Generic event tracking
   */
  trackEvent(eventType, data) {
    if (!this.isEnabled) return;

    const event = {
      type: eventType,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      url: window.location.href,
      ...data
    };

    this.eventQueue.push(event);

    // Auto-flush if queue is getting large
    if (this.eventQueue.length >= this.batchSize * 2) {
      this.flushEventQueue();
    }
  }

  /**
   * Track session end
   */
  trackSessionEnd() {
    const sessionDuration = Date.now() - this.sessionData.startTime;
    
    this.trackEvent(MONITORING_EVENTS.SESSION_END, {
      sessionId: this.sessionData.sessionId,
      duration: sessionDuration,
      pageViews: this.sessionData.pageViews,
      userActions: this.sessionData.userActions,
      apiCalls: this.sessionData.apiCalls,
      errors: this.sessionData.errors
    });

    // Force flush remaining events
    this.flushEventQueue();
    this.flushPerformanceQueue();
  }

  /**
   * Flush event queue
   */
  flushEventQueue() {
    if (this.eventQueue.length === 0) return;

    const events = this.eventQueue.splice(0, this.batchSize);
    
    if (process.env.NODE_ENV === 'development') {
      console.group('📊 Monitoring Events');
      events.forEach(event => {
        console.log(`${event.type}:`, event);
      });
      console.groupEnd();
    }

    // TODO: Send to monitoring service (CloudWatch, DataDog, etc.)
    // this.sendToMonitoringService(events);
  }

  /**
   * Flush performance queue
   */
  flushPerformanceQueue() {
    if (this.performanceQueue.length === 0) return;

    const metrics = this.performanceQueue.splice(0, this.batchSize);
    
    if (process.env.NODE_ENV === 'development') {
      console.group('⚡ Performance Metrics');
      metrics.forEach(metric => {
        console.log(`${metric.metric}: ${metric.value}ms`, metric);
      });
      console.groupEnd();
    }

    // TODO: Send to performance monitoring service
    // this.sendToPerformanceService(metrics);
  }

  /**
   * Get current session data
   */
  getSessionData() {
    return {
      ...this.sessionData,
      currentDuration: Date.now() - this.sessionData.startTime
    };
  }

  /**
   * Get monitoring statistics
   */
  getStatistics() {
    return {
      session: this.getSessionData(),
      queueSizes: {
        events: this.eventQueue.length,
        performance: this.performanceQueue.length
      },
      isEnabled: this.isEnabled
    };
  }

  /**
   * Enable/disable monitoring
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log(`📊 Monitoring ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Create singleton instance
const monitoringService = new MonitoringService();

// Export convenience methods
export const trackPageView = (path, metadata) => monitoringService.trackPageView(path, metadata);
export const trackUserAction = (action, category, metadata) => monitoringService.trackUserAction(action, category, metadata);
export const trackApiCall = (endpoint, method, duration, status, metadata) => monitoringService.trackApiCall(endpoint, method, duration, status, metadata);
export const trackError = (error, context, severity, metadata) => monitoringService.trackError(error, context, severity, metadata);
export const trackPerformance = (metric, value, metadata) => monitoringService.trackPerformance(metric, value, metadata);
export const trackFeatureUsage = (feature, action, metadata) => monitoringService.trackFeatureUsage(feature, action, metadata);
export const trackChatMessage = (messageLength, responseTime, success, metadata) => monitoringService.trackChatMessage(messageLength, responseTime, success, metadata);
export const trackSearch = (query, resultCount, searchTime, metadata) => monitoringService.trackSearch(query, resultCount, searchTime, metadata);

// Export monitoring service instance
export default monitoringService; 